import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Lock, Loader2, RefreshCw, Users, ClipboardList, KeyRound, Eye } from "lucide-react";
import { adminLogin, getAdminData, getAdminSubmission } from "@/lib/api";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const StatusBadge = ({ ok, yes = "Yes", no = "No" }) => (
  <Badge className={ok ? "bg-brand-green text-white hover:bg-brand-green" : "bg-brand-bone text-brand-ink/60 border border-border hover:bg-brand-bone"}>
    {ok ? yes : no}
  </Badge>
);

export default function Admin() {
  const [password, setPassword] = useState(sessionStorage.getItem("admin_pw") || "");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const viewSubmission = async (id) => {
    setDetailLoading(true);
    setDetail({});
    try {
      const d = await getAdminSubmission(password, id);
      setDetail(d);
    } catch (e) {
      toast.error("Could not load submission.");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const load = async (pw) => {
    setLoading(true);
    try {
      const d = await getAdminData(pw);
      setData(d);
      setAuthed(true);
      sessionStorage.setItem("admin_pw", pw);
    } catch (e) {
      toast.error("Invalid admin password.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async () => {
    if (!password.trim()) return;
    setLoading(true);
    try {
      await adminLogin(password.trim());
      await load(password.trim());
    } catch (e) {
      toast.error("Invalid admin password.");
      setLoading(false);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-brand-bone flex items-center justify-center px-4">
        <div className="max-w-sm w-full rounded-2xl border border-border bg-white p-8 shadow-sm" data-testid="admin-login-card">
          <div className="w-14 h-14 rounded-full bg-brand-green/10 flex items-center justify-center text-brand-green mx-auto mb-5">
            <Lock className="w-7 h-7" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-center">Admin Login</h1>
          <p className="text-brand-ink/60 text-center mt-1 text-sm">Enter your admin password.</p>
          <div className="mt-6 space-y-2">
            <Label htmlFor="admin-pw">Password</Label>
            <Input
              id="admin-pw"
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && login()}
            />
          </div>
          <Button data-testid="admin-login-btn" onClick={login} disabled={loading} className="w-full mt-5 rounded-full bg-brand-green hover:bg-brand-greenDark text-white">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log in"}
          </Button>
        </div>
      </div>
    );
  }

  const stats = data?.stats || {};

  return (
    <div className="min-h-screen bg-brand-bone">
      <div className="border-b border-border bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="font-heading font-extrabold text-lg">
            <span className="text-brand-green">3E</span> <span className="text-brand-gold">Admin</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => load(password)} className="rounded-full" data-testid="admin-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { icon: Users, label: "Paid customers", value: stats.total_paid ?? 0 },
            { icon: ClipboardList, label: "Survey submissions", value: stats.total_submissions ?? 0 },
            { icon: KeyRound, label: "Access code uses", value: stats.total_code_uses ?? 0 },
          ].map((s) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-border bg-white p-5">
              <s.icon className="w-5 h-5 text-brand-green" />
              <div className="font-heading text-3xl font-extrabold mt-3">{s.value}</div>
              <div className="text-sm text-brand-ink/60">{s.label}</div>
            </motion.div>
          ))}
        </div>

        <Tabs defaultValue="payments">
          <TabsList className="bg-white border border-border">
            <TabsTrigger value="payments" data-testid="tab-payments">Paid Customers</TabsTrigger>
            <TabsTrigger value="submissions" data-testid="tab-submissions">Completed Audits</TabsTrigger>
            <TabsTrigger value="codes" data-testid="tab-codes">Access Codes</TabsTrigger>
          </TabsList>

          <TabsContent value="payments">
            <div className="rounded-xl border border-border bg-white overflow-x-auto" data-testid="payments-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Product</TableHead>
                    <TableHead>Amount</TableHead><TableHead>Payment Status</TableHead><TableHead>Email Sent</TableHead><TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data?.payments || []).map((p) => (
                    <TableRow key={p.session_id}>
                      <TableCell className="font-medium">{p.buyer_name || "—"}</TableCell>
                      <TableCell>{p.buyer_email}</TableCell>
                      <TableCell>{p.product_code}</TableCell>
                      <TableCell>${p.amount}</TableCell>
                      <TableCell><StatusBadge ok={p.payment_status === "paid"} yes="Paid" no={p.payment_status} /></TableCell>
                      <TableCell><StatusBadge ok={p.email_sent} /></TableCell>
                      <TableCell className="text-sm text-brand-ink/60">{fmt(p.created_at)}</TableCell>
                    </TableRow>
                  ))}
                  {(!data?.payments || data.payments.length === 0) && (
                    <TableRow><TableCell colSpan={7} className="text-center text-brand-ink/40 py-8">No payments yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="submissions">
            <div className="rounded-xl border border-border bg-white overflow-x-auto" data-testid="submissions-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Business</TableHead>
                    <TableHead>Payment Status</TableHead><TableHead>Overall Score</TableHead>
                    <TableHead>Client Email</TableHead><TableHead>Admin Notified</TableHead>
                    <TableHead>Completed</TableHead><TableHead>Results</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data?.submissions || []).map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium">{s.name || "—"}</TableCell>
                      <TableCell>{s.email}</TableCell>
                      <TableCell>{s.business || "—"}</TableCell>
                      <TableCell className="capitalize">{s.payment_status || s.access_type}</TableCell>
                      <TableCell><Badge className="bg-brand-gold text-white hover:bg-brand-gold">{s.scorecard?.overall_score ?? "—"}/100</Badge></TableCell>
                      <TableCell><StatusBadge ok={s.email_sent} yes="Sent" no="Not sent" /></TableCell>
                      <TableCell><StatusBadge ok={s.admin_email_sent} yes="Sent" no="Not sent" /></TableCell>
                      <TableCell className="text-sm text-brand-ink/60">{fmt(s.completed_at)}</TableCell>
                      <TableCell>
                        <Button size="sm" variant="outline" className="rounded-full h-8" data-testid={`view-submission-${s.id}`} onClick={() => viewSubmission(s.id)}>
                          <Eye className="w-3.5 h-3.5 mr-1" /> View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!data?.submissions || data.submissions.length === 0) && (
                    <TableRow><TableCell colSpan={9} className="text-center text-brand-ink/40 py-8">No completed audits yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="codes">
            <div className="rounded-xl border border-border bg-white overflow-x-auto" data-testid="codes-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead><TableHead>Type</TableHead><TableHead>Used for Survey</TableHead><TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data?.access_codes || []).map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="font-mono">{c.code}</TableCell>
                      <TableCell className="capitalize">{c.type}</TableCell>
                      <TableCell><StatusBadge ok={c.used_for_survey} /></TableCell>
                      <TableCell className="text-sm text-brand-ink/60">{fmt(c.created_at)}</TableCell>
                    </TableRow>
                  ))}
                  {(!data?.access_codes || data.access_codes.length === 0) && (
                    <TableRow><TableCell colSpan={4} className="text-center text-brand-ink/40 py-8">No access code uses yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="submission-detail-dialog" className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Completed Audit Details</DialogTitle>
          </DialogHeader>
          {detailLoading || !detail || !detail.id ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-brand-green" /></div>
          ) : (
            <div className="space-y-6 text-sm">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                <div><span className="text-brand-ink/50">Name:</span> <strong>{detail.name || "—"}</strong></div>
                <div><span className="text-brand-ink/50">Email:</span> <strong>{detail.email || "—"}</strong></div>
                <div><span className="text-brand-ink/50">Business:</span> <strong>{detail.business || "—"}</strong></div>
                <div><span className="text-brand-ink/50">Phone:</span> <strong>{detail.phone || "Not collected"}</strong></div>
                <div><span className="text-brand-ink/50">Payment:</span> <strong className="capitalize">{detail.payment_status || detail.access_type}</strong></div>
                <div><span className="text-brand-ink/50">Product:</span> <strong>{detail.product_code || "—"}</strong></div>
                <div className="col-span-2"><span className="text-brand-ink/50">Stripe session:</span> <strong className="break-all">{detail.session_id || "N/A (access code)"}</strong></div>
                <div><span className="text-brand-ink/50">Client email:</span> <StatusBadge ok={detail.email_sent} yes="Sent" no="Not sent" /></div>
                <div><span className="text-brand-ink/50">Admin notified:</span> <StatusBadge ok={detail.admin_email_sent} yes="Sent" no="Not sent" /></div>
                <div className="col-span-2"><span className="text-brand-ink/50">Submitted:</span> <strong>{fmt(detail.completed_at)}</strong></div>
              </div>

              <div className="rounded-xl bg-brand-bone p-4">
                <p className="font-heading font-bold text-lg">Overall Leak Score: {detail.scorecard?.overall_score}/100</p>
                <div className="mt-3 grid grid-cols-2 gap-1">
                  {detail.scorecard?.categories?.map((c) => (
                    <div key={c.category} className="flex justify-between"><span className="text-brand-ink/70">{c.category}</span><strong>{c.score}/100</strong></div>
                  ))}
                </div>
              </div>

              <div>
                <p className="font-heading font-semibold text-brand-green mb-2">Top Leaks</p>
                <ul className="list-disc pl-5 space-y-1">
                  {detail.scorecard?.top_leaks?.map((l, i) => (<li key={i}><strong>{l.category}</strong> ({l.score}/100) — {l.why}</li>))}
                </ul>
              </div>

              <div>
                <p className="font-heading font-semibold text-brand-green mb-2">Recommended Next Steps</p>
                <ol className="list-decimal pl-5 space-y-1">
                  {detail.scorecard?.next_steps?.map((s, i) => (<li key={i}>{s}</li>))}
                </ol>
              </div>

              <div>
                <p className="font-heading font-semibold text-brand-green mb-2">Full Survey Answers</p>
                <div className="rounded-xl border border-border divide-y divide-border">
                  {Object.entries(detail.question_labels || {}).filter(([k]) => detail.answers?.[k] !== undefined && detail.answers?.[k] !== "").map(([k, label]) => (
                    <div key={k} className="flex gap-3 p-2">
                      <span className="text-brand-ink/55 w-1/2 shrink-0">{label}</span>
                      <span className="text-brand-ink">{String(detail.answers[k])}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
